import { observable, action, computed, makeObservable } from 'mobx'

import ganApi from '@client/api/gan'
import { fetchBlocks } from '@client/components/Sidebar/SidebarDefault/TabBuild/CustomBlocks/model'
import { BLOCK_TYPES } from '../Block/constants'

// The backend's own messages for a rejected save, e.g. a 400's
// `{ "name": ["Ensure this field has no more than 255 characters."] }` or a
// 403's `{ "detail": "..." }`. Empty for a 5xx or a network failure, which
// say nothing the user can act on.
export const describeSaveError = (error) => {
  const data = error && error.data
  if (!data || typeof data !== 'object' || error.status >= 500) {
    return ''
  }
  return Object.values(data)
    .flat()
    .filter((message) => typeof message === 'string')
    .join(' ')
}

export class SaveAsBlockDialogModel {
  @observable show = false
  @observable name = ''
  @observable saved = false
  @observable error = false
  @observable errorDetails = ''
  @observable saving = false
  @observable block = false
  @observable itemType = null

  constructor() {
    makeObservable(this)
  }

  @computed get canBeSaved() {
    return this.name.trim().length > 0 && !!this.block && !!this.itemType
  }

  @action showDialog = (block, itemType) => {
    this.show = true
    this.name = ''
    this.saved = false
    this.error = false
    this.errorDetails = ''
    this.saving = false
    this.block = block
    this.itemType = itemType
  }

  @action setName = (name) => {
    this.name = name
  }

  @action setSaved = (saved) => {
    this.saved = saved
  }

  @action setError = (error, details = '') => {
    this.error = error
    this.errorDetails = details
  }

  @action setSaving = (saving) => {
    this.saving = saving
  }

  // A failure shows in the dialog, which stays open so the user can retry
  // (FE-1489). createBlock rejects without opening the document's "reconnect"
  // dialog.
  @action save = async () => {
    this.saving = true
    this.setError(false)

    if (this.block) {
      const serializedBlock = this.block.serialize()
      if (!serializedBlock) {
        this.setError(true)
        this.saving = false
        return
      }
      let type = serializedBlock.type
      if (
        serializedBlock.type === BLOCK_TYPES.FIELD &&
        serializedBlock.fieldType
      ) {
        type = serializedBlock.fieldType
      }
      if (
        serializedBlock.type === BLOCK_TYPES.SECTION &&
        serializedBlock.sectionType
      ) {
        type = serializedBlock.sectionType
      }
      const payload = {
        type: type,
        block: serializedBlock,
        name: this.name,
        item_type: this.itemType,
      }
      let result
      try {
        result = await ganApi.createBlock(payload)
      } catch (e) {
        this.setError(true, describeSaveError(e))
        this.setSaving(false)
        return
      }
      if (result && result.id) {
        this.setSaved(true)
        await fetchBlocks(this.itemType)
      } else {
        this.setError(true)
      }
    }

    this.setSaving(false)
  }

  @action hideDialog = () => {
    this.show = false
    this.name = ''
    this.saved = false
    this.error = false
    this.errorDetails = ''
    this.saving = false
    this.block = false
    this.itemType = null
  }
}

export const saveAsBlockDialogModel = new SaveAsBlockDialogModel()
export const showDialog = saveAsBlockDialogModel.showDialog
export const hideDialog = saveAsBlockDialogModel.hideDialog
